import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, KeyRound, ArrowRight, Theater } from "lucide-react";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };
const STAGE_IMG = "https://images.unsplash.com/photo-1613210434051-4b00d62d03fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxlbXB0eSUyMHRoZWF0ZXIlMjBzdGFnZSUyMGRhcmslMjBzcG90bGlnaHR8ZW58MHx8fHwxNzgzMzU0OTI3fDA&ixlib=rb-4.1.0&q=85";

export default function Enroll() {
  const { selectProduction, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1: code, 2: form
  const [code, setCode] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [prodPreview, setProdPreview] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    email: "", password: "", name: "", role: "cast", phone: "", character: "",
  });

  const previewCode = async (e) => {
    e?.preventDefault?.();
    setError("");
    setPreviewing(true);
    try {
      const { data } = await api.get("/enroll/preview", { params: { code: code.toUpperCase().trim() } });
      setProdPreview(data);
      setStep(2);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Invalid code");
    } finally {
      setPreviewing(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/enroll", { code: code.toUpperCase().trim(), ...form });
      if (data.token) localStorage.setItem("sc_token", data.token);
      await refresh();
      selectProduction(data.production.id);
      nav("/dashboard", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to enroll");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#0A0A0A]">
      <div className="relative hidden md:flex flex-col justify-between p-10 overflow-hidden border-r border-white/10">
        <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{ backgroundImage: `url(${STAGE_IMG})` }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
        <div className="absolute inset-0 grain-overlay opacity-40" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md border border-white/15 grid place-items-center bg-[#121212]">
              <Sparkles className="w-4 h-4 text-gold" />
            </div>
            <div>
              <div className="font-display text-2xl tracking-tight">StageCall</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-400">Self-Enroll</div>
            </div>
          </div>
        </div>
        <div className="relative max-w-md">
          <h1 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight font-light">
            Got a code?<br />
            <span className="text-gold">Step into the show.</span>
          </h1>
          <p className="mt-5 text-zinc-400 text-base leading-relaxed">
            Your production team gave you a 6-character enrollment code. Enter it here and you'll join the company in a moment.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md space-y-6 animate-fade-up">
          <div className="md:hidden flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-gold" /><span className="font-display text-2xl">StageCall</span>
          </div>

          {step === 1 && (
            <form onSubmit={previewCode} className="space-y-6" data-testid="enroll-code-form">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Step 1 / 2</div>
                <h2 className="mt-2 font-display text-3xl tracking-tight font-medium">Enter your enrollment code.</h2>
                <p className="mt-2 text-sm text-zinc-400">Six characters, letters and numbers. Case doesn't matter.</p>
              </div>
              <div>
                <Label htmlFor="code" className="text-zinc-300 text-xs uppercase tracking-widest">Code</Label>
                <div className="mt-2 relative">
                  <KeyRound className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    id="code"
                    data-testid="enroll-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    maxLength={8}
                    required
                    autoFocus
                    className="pl-9 bg-[#121212] border-white/10 h-12 font-mono tracking-[0.4em] text-lg uppercase focus:border-gold focus:ring-1 focus:ring-gold"
                    placeholder="ABC123"
                  />
                </div>
              </div>
              {error && <div data-testid="enroll-error" className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded-md px-3 py-2">{error}</div>}
              <button data-testid="enroll-continue-btn" type="submit" disabled={previewing} style={goldBtn} className="w-full h-11 rounded-md font-semibold hover:brightness-110">
                {previewing ? "Checking…" : "Continue"}
              </button>
              <div className="text-sm text-zinc-500 text-center">
                Already have an account? <Link to="/login" className="text-gold hover:underline">Sign in</Link>
              </div>
            </form>
          )}

          {step === 2 && prodPreview && (
            <form onSubmit={submit} className="space-y-5" data-testid="enroll-form">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Step 2 / 2</div>
                <h2 className="mt-2 font-display text-3xl tracking-tight font-medium">Complete your profile.</h2>
              </div>

              <div className="rounded-md border border-gold/30 bg-gold/5 p-4">
                <div className="text-[10px] uppercase tracking-widest text-gold">You're joining</div>
                <div className="mt-1 flex items-center gap-2">
                  <Theater className="w-4 h-4 text-gold" />
                  <div className="font-display text-xl">{prodPreview.title}</div>
                </div>
                {prodPreview.tagline && <div className="mt-1 text-sm text-zinc-400">{prodPreview.tagline}</div>}
                <div className="mt-2 text-xs text-zinc-500 flex flex-wrap gap-3">
                  {prodPreview.venue && <span>{prodPreview.venue}</span>}
                  {prodPreview.director && <span>Dir. {prodPreview.director}</span>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Full name</Label><Input data-testid="enroll-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#121212] border-white/10 h-11 focus:border-gold focus:ring-1 focus:ring-gold" /></div>
                <div className="col-span-2"><Label>Email</Label><Input data-testid="enroll-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#121212] border-white/10 h-11 focus:border-gold focus:ring-1 focus:ring-gold" /></div>
                <div className="col-span-2"><Label>Password</Label><Input data-testid="enroll-password-input" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#121212] border-white/10 h-11 focus:border-gold focus:ring-1 focus:ring-gold" /></div>
                <div className="col-span-2 rounded-md border border-white/10 bg-white/5 p-3 text-xs text-zinc-400">
                  You'll join as <span className="text-gold font-semibold">Cast</span>. If your role is Production Staff, Support Staff, or Producer, your Org Admin will promote you after you enroll.
                </div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#121212] border-white/10 h-11 focus:border-gold focus:ring-1 focus:ring-gold" /></div>
                <div className="col-span-2"><Label>Character / Role note</Label><Input data-testid="enroll-character-input" value={form.character} onChange={(e) => setForm({ ...form, character: e.target.value })} placeholder="Ex: Cinderella, Lighting Op" className="bg-[#121212] border-white/10 h-11 focus:border-gold focus:ring-1 focus:ring-gold" /></div>
              </div>

              {error && <div data-testid="enroll-error-2" className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded-md px-3 py-2">{error}</div>}

              <div className="flex items-center gap-2">
                <button type="button" onClick={() => { setStep(1); setProdPreview(null); }} className="h-11 px-3 rounded-md border border-white/15 text-zinc-300 hover:text-white hover:bg-white/5">Back</button>
                <button data-testid="enroll-submit-btn" type="submit" disabled={submitting} style={goldBtn} className="flex-1 h-11 rounded-md font-semibold hover:brightness-110 inline-flex items-center justify-center gap-1.5">
                  {submitting ? "Enrolling…" : (<>Join production <ArrowRight className="w-4 h-4" /></>)}
                </button>
              </div>
              <div className="text-xs text-zinc-500">
                If your production admin already made an account for this email, use the enroll code to <span className="text-gold">claim it</span> — the password you enter here becomes your login. Otherwise a new account will be created and added to <span className="text-zinc-300">{prodPreview.title}</span>.
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
